import { AppUtil, TikTokAd, DateUtil, DateRangeUtil } from 'kuaidong-lib'

import {
  Clazz,
  Teacher,
  TeacherInfo,
  SummaryStats,
  ITeacherForm,
  TeacherAuthForm,
  ITeacherAuthList,
  TeacherAuthRecord,
  UpdateTeacherInfo,
  ITeacherCreateForm,
} from 'kuaidong-model'

export class ATeacher {
  public static async find(): Promise<Teacher> {
    return AppUtil.httpGET('/account/teacher_info').then((res) => {
      const school = res?.school

      if (school?.verified_start_date && school?.verified_end_date) {
        res.school.verified = DateRangeUtil.in(
          DateUtil.beginningOfDay(),
          school.verified_start_date,
          school.verified_end_date
        )
      }

      return res
    })
  }

  public static async save(form: ITeacherForm): Promise<Teacher> {
    if (form.user_id) {
      return AppUtil.httpPUT('/teacher/modify', {
        teacher_id: form.teacher_id,
        teacher_user_id: form.user_id,
        teacher_name: form.teacher_name,
        head_img_url: form.head_img_url,
        teacher_role: form.role,
        binding_classes: form.clazzes,
        role_type: form.is_admin ? 'ADMIN' : null,
      })
    } else {
      return AppUtil.httpPOST('/web/teacher_manage/apply/teacher', {
        teacher_name: form.teacher_name,
        classes: form.clazzes,
        phone: form.phone,
        role: form.role,
      })
    }
  }

  public static async remove(userId: string) {
    return AppUtil.httpDelete('/teacher/delete', { teacher_user_id: userId })
  }

  public static async registerSimple(params: any) {
    return AppUtil.httpPOST(`/cel/operation/ad/register_apply?channel=saas-quick`, params)
  }

  public static async applyAuth(teacherAuthForm: TeacherAuthForm, channel: string): Promise<boolean> {
    const { id, name, province, city, district } = teacherAuthForm.school
    const params = {
      phone: teacherAuthForm.phone,
      channel: teacherAuthForm.channel,
      verify_code: teacherAuthForm.verificationCode,
      invite_code: teacherAuthForm.invite_code,
      form_data: {
        teacher_name: teacherAuthForm.name,
        ...(id ? { school_id: id } : {}),
        school_name: name,
        province,
        city,
        district,
        classes: teacherAuthForm.classes.map(({ gradeNo: grade_no, classNo: class_no, noteName: note_name }) => ({
          grade_no,
          class_no,
          note_name,
        })),
        purpose: teacherAuthForm.purpose,
        identify: teacherAuthForm.identify,
        no_need_code: teacherAuthForm.isWechat,
      },
    }

    return AppUtil.httpPOST(`/account/verify?channel=${await TikTokAd.channelWithEnv(channel)}`, params)
  }

  public static async applyAuthSimple(params: any): Promise<boolean> {
    return AppUtil.httpPOST(`/account/verify?channel=${params.channel ? params.channel : 'sass'}`, params)
  }

  public static async allAuthRecords(): Promise<TeacherAuthRecord[]> {
    return AppUtil.httpGET('/account/verify')
  }

  static async joinClazz({
    gradeNo,
    classNo,
    noteName,
    teacherId,
  }: {
    gradeNo: number
    classNo?: number
    noteName?: string
    teacherId?: string
  }): Promise<Clazz | void> {
    const params = {
      bind_classes: [{ grade_no: gradeNo, class_no: classNo, note_name: noteName }],
      teacher_id: teacherId,
    }
    return AppUtil.httpPOST('/clazz/setting/teacher_operation', params)
  }

  static async exitClazz(clazzId: number, teacher_id?: string): Promise<void> {
    const params = {
      class_ids: [clazzId],
      teacher_id, //不传就是退出自己
    }
    return AppUtil.httpDelete('/clazz/setting/teacher_operation', params)
  }

  static async getStudentInvitationUrl(clazzId: number): Promise<string> {
    return AppUtil.httpGET('/clazz/setting/student_invitation_url', { class_id: clazzId })
  }

  static async getStudentInvitationUrlPerSchool(extra_params?: Record<string, any>): Promise<string> {
    return (await AppUtil.httpPOST('/schools/invite_url', { extra_params })).url
  }

  static readonly ShareSceneClazzInvite = 'ClazzInvite' // 班级邀请进班

  static async getShareCode(scene: string): Promise<string> {
    const result = await AppUtil.httpGET('/async/teacher/share_code', { scene })
    return result.share_code
  }

  static async summaryStats(): Promise<SummaryStats> {
    return AppUtil.httpGET('/cel/teacher/summary_stat')
  }

  static async allClasses(): Promise<Clazz[]> {
    return AppUtil.httpGET('/cel/teacher/school/clazzes')
  }

  static async allTeachers(): Promise<{ teacher_id: string; teacher_name: string }[]> {
    return AppUtil.httpGET('/cel/teacher/school_teachers')
  }

  static async ownClasses(): Promise<Clazz[]> {
    return AppUtil.httpGET('/cel/clazzes/teacher_own')
  }

  // 教师头像上传修改 api
  static async fetchTeacherInfo(): Promise<TeacherInfo> {
    return AppUtil.httpGET('/async/teacher/info/update')
  }

  static async updateTeacherInfo(updateInfo: UpdateTeacherInfo) {
    return AppUtil.httpPOST('/async/teacher/info/update', updateInfo)
  }

  static webApplyAuthTeacher(teachersInfo: ITeacherCreateForm) {
    return AppUtil.httpPOST('/web/teacher_manage/apply/teacher', teachersInfo)
  }

  static webFetchAuthTeacherList(): Promise<ITeacherAuthList> {
    return AppUtil.httpGET('/web/teacher_manage/teachers')
  }

  static webClassesExtra(teacher_id: string): Promise<Array<Clazz>> {
    return AppUtil.httpGET('/web/classes/extra', { teacher_id })
  }

  static webTeacherPassApply(apply_id: string): Promise<any> {
    return AppUtil.httpPOST('/web/teacher_manage/pass/apply', { apply_id: apply_id })
  }

  static webTeacherJoinClazz({
    gradeNo,
    classNo,
    teacherId,
  }: {
    gradeNo: number
    classNo: number
    teacherId: string
  }): Promise<any> {
    const params = {
      bind_classes: [{ grade_no: gradeNo, class_no: classNo }],
      teacher_id: teacherId,
    }
    return AppUtil.httpPOST('/web/teacher_manage/bind/classes', params)
  }

  static webTeacherDeleteClazz({ clazzId, teacherId }: { clazzId: number; teacherId: string }): Promise<any> {
    const params = {
      class_ids: [clazzId],
      teacher_id: teacherId,
    }
    return AppUtil.httpDelete('/web/teacher_manage/bind/classes', params)
  }

  static webTeacherRejectAuth({ applyId, rejectReason }: { applyId: string; rejectReason: string }): Promise<any> {
    const params = {
      apply_id: applyId,
      reject_reason: rejectReason,
    }
    return AppUtil.httpPOST('/web/teacher_manage/reject/apply', params)
  }

  static delTeacherForAdmin(teacher_user_id: string) {
    return AppUtil.httpDelete('/teacher/delete', {
      teacher_user_id,
    })
  }

  static assistant(): Promise<{ wechat_url: string }> {
    return AppUtil.httpGET('/teacher/assistant')
  }
}
